// ownerContactClient — PUBLIC, token-gated client for the Owner Contact Addendum
// intake (the 4th owner-onboarding step, chained after a fully-executed EPMA).
//
// Like intakeClient / ownerIntakeClient this surface is token-only (NO Bearer): the
// token in the path is the sole credential, so calls go straight to `fetch`. Wire
// shapes are 1:1 with the backend contract in:
//   • apps/api/src/routes/ownerContactIntake.ts   (OwnerContactFormView + submit)
//   • apps/api/src/lib/ownerContactIntake.ts        (OwnerContactApplicable)
//   • apps/api/src/types/db.ts                       (OwnerContactAnswers + parts)
//
//   - GET  /api/owner-contact/:token → OwnerContactFormView
//   - POST /api/owner-contact/:token → progress save (full view) OR, on a winning
//     completion, { state:'ready', signUrl }.

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

// ---------------------------------------------------------------------------
// Answer shapes (mirror apps/api/src/types/db.ts — NO TIN anywhere)
// ---------------------------------------------------------------------------

/** How funds + year-end 1099 are handled for the whole ownership. */
export type OwnerContactHandlingMode = 'single' | 'one_handles' | 'split';

/**
 * A member/trustee CONTACT behind an ENTITY owner — the OTHER members/trustees the
 * itemizer flagged (needsMemberCollection). CONTACTS ONLY: they don't sign, carry no
 * %, no funds/tax designation, no W-9. Rendered under the entity + surfaced to staff.
 */
export interface OwnerContactMemberContact {
  firstName?: string;
  lastName?: string;
  role?: string;
  phone?: string;
  email?: string;
  /**
   * Spec §14: should this member see statements and financials in the AppFolio OWNER
   * PORTAL? Yes → their email is added to the entity's single AppFolio owner (portal
   * access follows the email). Only asked once the member has an email. Absent = not
   * answered (treated as no).
   */
  portalAccess?: boolean;
}

/**
 * One CONFIRMABLE PERSON in the itemized roster (the operative roster is the itemized
 * PEOPLE, which can exceed the raw EPMA — a packed couple → two). Confirm-don't-re-ask:
 * the identity is seeded (frozen, editable via Edit); `phone` is the only genuinely-new
 * field. `email` is the confirmed/corrected address.
 */
export interface OwnerContactOwnerAnswer {
  /** Seeded first name (frozen; editable via Edit). Individual OR entity signer. */
  firstName?: string;
  /** Seeded last name (frozen; editable). */
  lastName?: string;
  /** 'individual' or 'entity' (this person is the entity's authorized signer). */
  kind?: 'individual' | 'entity';
  /** Entity legal name (entity people only). */
  entityName?: string;
  /** The entity signer's title/capacity (entity people only). */
  signerTitle?: string;
  /** The raw EPMA owner this person was itemized from (provenance; groups a split). */
  sourceIndex?: number;
  /** Bedrock split confidence 0..1 (low → the UI flags "we read this as two people"). */
  confidence?: number;
  /** True when this person came from splitting a packed owner (offer Merge-back). */
  fromSplit?: boolean;
  /** Entity people: collect the OTHER members/trustees. */
  needsMemberCollection?: boolean;
  /** Entity people: the added member/trustee CONTACTS (non-signing, non-tax). */
  members?: OwnerContactMemberContact[];
  phone?: string;
  email?: string;
  /** SPLIT only: this person's ownership % (the column sums to 100). */
  ownershipPercent?: number;
  /** SPLIT only: this person's share of disbursed funds (the column sums to 100). */
  fundsPercent?: number;
  /** Multi-owner: receives owner statements? (single-owner: silent yes). */
  receivesStatements?: boolean;
  /** Multi-owner: receives email communications? (single-owner: silent yes). */
  receivesEmail?: boolean;
}

/** Where the owner's disbursed funds go (NO account/routing numbers — a label only). */
export interface OwnerContactDisbursement {
  mode?: OwnerContactHandlingMode;
  /** single / one_handles: the 0-based owner index funds are disbursed to. */
  fundsOwnerIndex?: number;
  /** Optional free-text label for the receiving account (e.g. "joint checking"). */
  fundsAccountLabel?: string;
}

/** Who the year-end 1099 is issued to (NO TIN — just which owner, by index). */
export interface OwnerContactTaxReporting {
  ownerIndex?: number;
}

/** How the owner found WHP — capture-only (source required, referrer detail optional). */
export interface OwnerContactReferral {
  source?: string;
  referrerDetail?: string;
}

/**
 * A NON-owner emergency/backup contact WHP can reach for urgent questions when the
 * owner(s) are unreachable. Not an owner: no %, no designation, no signature, no W-9.
 */
export interface OwnerContactBackupContact {
  name: string;
  relationship: string;
  phone?: string;
  email?: string;
}

/**
 * One married-filing-jointly couple (spec §13.2), by index into the people roster
 * (= perOwner[].ownerIndex). `designee` is the spouse who receives the couple's
 * funds AND the year-end 1099 — either spouse, whatever the ownership split.
 */
export interface OwnerContactCouple {
  a: number;
  b: number;
  designee?: number;
}

/** The spouses answer; `couples: []` means "no married-filing-jointly couples here". */
export interface OwnerContactSpouses {
  answered: true;
  couples: OwnerContactCouple[];
}

/** The RAW owner-contact answers accumulated across saves. */
export interface OwnerContactAnswers {
  owners?: OwnerContactOwnerAnswer[];
  disbursement?: OwnerContactDisbursement;
  taxReporting?: OwnerContactTaxReporting;
  referral?: OwnerContactReferral;
  backupContacts?: OwnerContactBackupContact[];
  /** §13.2 — absent = not answered yet (and on every legacy intake). */
  spouses?: OwnerContactSpouses;
  /**
   * City of Walla Walla utility account number — OPTIONAL, ≤ 40 chars, asked only
   * when the view says showCityUtilityAccountNo (docs/city-utility-agreement-plan.md §4).
   */
  cityUtilityAccountNo?: string;
}

// ---------------------------------------------------------------------------
// The server-computed applicable-questions model (the SPA renders THIS; the server
// decides the fork — never recompute it client-side).
// ---------------------------------------------------------------------------

export interface OwnerContactApplicableOwner {
  ownerIndex: number;
  role: string;
  /** Party name for the roster ("Jane Doe" or "Maple Holdings, LLC"). */
  name: string;
  /** The effective (confirmed-or-seeded) email. */
  email: string;
  /** 'individual' or 'entity' (an entity person is the entity's authorized signer). */
  kind: 'individual' | 'entity';
  /** Entity legal name (entity people only; '' otherwise). */
  entityName: string;
  /** Bedrock split confidence 0..1 (1 = firm). */
  confidence: number;
  /** True when this person came from splitting a packed owner (offer Merge-back). */
  fromSplit: boolean;
  /** Entity people: show the "add other members/trustees" section. */
  needsMemberCollection: boolean;
  /**
   * Spec §14: ask each of this person's member rows about owner-portal access
   * (true for entity people, full and contact-only scope). Absent on an API without
   * §14 → the question is not shown and `portalAccess` is never posted.
   */
  askMemberPortalAccess?: boolean;
  /** How many member/trustee contacts have been added for this (entity) person. */
  memberCount: number;
  /** The raw EPMA owner this person was itemized from (client groups a split by it). */
  sourceIndex: number;
  /** The raw EPMA owner's first/last name — restores the original packed field on a
   *  merge-back (so "Miles & Nicole" / "Scruggs" returns, not "Miles Nicole"). */
  sourceFirstName: string;
  sourceLastName: string;
}

/**
 * A TAX UNIT (spec §13.2, server lib/ownerTaxUnits.ts): a married-filing-jointly
 * couple is one unit; everyone else is their own. One unit → it receives 100% of
 * the funds; two or more → each unit's funds share equals its ownership share.
 */
export interface OwnerTaxUnit {
  /** Roster indices (a couple has two). */
  members: number[];
  kind: 'couple' | 'individual' | 'entity';
  /** Who receives the unit's funds + 1099; null = a couple that hasn't chosen yet. */
  designee: number | null;
  /** Sum of the members' ownership %; null while any member's share is missing. */
  ownershipPercent: number | null;
  /** 1 unit → 100. ≥2 units → equals ownershipPercent. */
  fundsPercent: number | null;
}

export interface OwnerContactApplicable {
  ownerCount: number;
  /** The resolved handling mode (single / one_handles / split). */
  mode: OwnerContactHandlingMode;
  /** Multi-owner → the "one of us handles it vs split" fork is asked. */
  showHandlingFork: boolean;
  /** Split (multi-owner) → the ownership%/funds% grids + per-owner toggles apply. */
  showPercentages: boolean;
  /** single OR one_handles → a single funds+1099 designee is asked. */
  showDesignee: boolean;
  /** Per-owner confirm rows (phone is the only genuinely-new field). */
  perOwner: OwnerContactApplicableOwner[];
  referralRequired: boolean;
  // --- spec §13.3. OPTIONAL on purpose: a server without them renders today's
  // form unchanged (lib/ownerContactFunds.ts fundsFlow → 'legacy'). ---
  /** false for a contact-only intake (existing owner, override): no banking / 1099 section. */
  showFundsSection?: boolean;
  /** 'yes_no' = exactly two individuals; 'pairs' = more than two; 'none' = not asked. */
  spousesQuestion?: 'none' | 'yes_no' | 'pairs';
  /** Derived from the CURRENT answers (re-computed on every save). */
  taxUnits?: OwnerTaxUnit[];
  /** ≥2 tax units → funds follow ownership (funds shares read-only). */
  fundsFollowOwnership?: boolean;
}

// ---------------------------------------------------------------------------
// The public form VIEW (server-driven; never recomputed client-side).
// ---------------------------------------------------------------------------

/**
 *   • 'fillable'    — open for this owner to complete.
 *   • 'generating'  — a completion won the lock; the addendum is being written.
 *   • 'ready'       — the addendum is out for signature (signUrl set).
 *   • 'unavailable' — bad/dormant token, cancelled, or the owner isn't on the record.
 */
export type OwnerContactState = 'fillable' | 'generating' | 'ready' | 'unavailable';

export interface OwnerContactFormView {
  state: OwnerContactState;
  propertyAddress: string;
  /** Joined owner party names — the "you and the other owners" framing. */
  ownerNames: string;
  /** The owner this link is for (display), or null on an unavailable view. */
  owner: { role: string; name: string } | null;
  /** The server-computed applicable questions (fillable state; null otherwise). */
  applicable: OwnerContactApplicable | null;
  /** The owner's saved answers so far (resumable). */
  answers: OwnerContactAnswers;
  /** When state === 'ready': this owner's direct sign-page URL (absolute). */
  signUrl?: string;
  /**
   * The property is inside the City of Walla Walla limits → show the optional City
   * utility account number field (GET view only; kept across progress saves).
   */
  showCityUtilityAccountNo?: boolean;
}

/**
 * POST result. A progress save echoes a full OwnerContactFormView. A WINNING
 * completion returns the minimal `{ state:'ready', signUrl }` (no other view fields).
 * We type it as a partial view with a required state so both shapes are handled.
 */
export interface OwnerContactSubmitResult {
  state: OwnerContactState;
  signUrl?: string;
  propertyAddress?: string;
  ownerNames?: string;
  owner?: { role: string; name: string } | null;
  applicable?: OwnerContactApplicable | null;
  answers?: OwnerContactAnswers;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

/**
 * Error carrying the parsed body so the page can read `problems` (a completion-
 * validation 400) or `error` (injection 400 / 409 / 503 / 500).
 */
export class OwnerContactApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`Owner contact API error ${status}`);
    this.name = 'OwnerContactApiError';
  }

  /** The server's human message, if any. */
  get errorMessage(): string | null {
    return (this.body as { error?: string } | null)?.error ?? null;
  }

  /** The completion-validation problem messages on a 400 (empty otherwise). */
  get problems(): string[] {
    const p = (this.body as { problems?: unknown } | null)?.problems;
    return Array.isArray(p) ? (p as string[]) : [];
  }
}

async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && typeof init.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty */
    }
    throw new OwnerContactApiError(res.status, body);
  }
  return (await res.json()) as T;
}

/** GET the public, token-gated form view. */
export function getOwnerContact(token: string): Promise<OwnerContactFormView> {
  return publicRequest<OwnerContactFormView>(
    `/api/owner-contact/${encodeURIComponent(token)}`,
  );
}

/**
 * POST progress or completion. `{ answers }` saves partial progress (echoes the
 * refreshed view — the server re-computes `applicable`). Adding `{ complete:true }`
 * attempts the first-completion-wins lock + auto-generate. A 400 (validation /
 * injection) throws OwnerContactApiError (read .problems / .errorMessage).
 */
export function submitOwnerContact(
  token: string,
  body: { answers?: OwnerContactAnswers; complete?: boolean },
): Promise<OwnerContactSubmitResult> {
  return publicRequest<OwnerContactSubmitResult>(
    `/api/owner-contact/${encodeURIComponent(token)}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}
